// Phase 2.11 — Optional due date picker for assessment findings.
// Thin wrapper around shadcn calendar; stores ISO yyyy-mm-dd.
import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Props {
  value: string | null;
  onChange: (iso: string | null) => void;
  disabled?: boolean;
  className?: string;
}

export const DueDatePicker: React.FC<Props> = ({ value, onChange, disabled, className }) => {
  const [open, setOpen] = useState(false);
  const date = value ? parseISO(value) : undefined;

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className={cn("h-7 text-xs gap-1 justify-start", !date && "text-muted-foreground")}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {date ? format(date, "MMM d, yyyy") : "Due date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 pointer-events-auto" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => {
              onChange(d ? format(d, "yyyy-MM-dd") : null);
              setOpen(false);
            }}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
      {value && !disabled && (
        <button
          type="button"
          className="rounded-full hover:bg-muted p-0.5 text-muted-foreground"
          onClick={() => onChange(null)}
          aria-label="Clear due date"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};
